import { cn } from "@babackapp/customer-ui/lib/utils";
import { UploadCloudIcon, XIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

type Props = {
  onChange?: (files: File[]) => void;
  multiple?: boolean;
  className?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
};

type Preview = { file: File; url: string };

export function ImageInput({
  onChange,
  multiple = true,
  className,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previews, setPreviews] = useState<Preview[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    return () => {
      previews.forEach((p) => URL.revokeObjectURL(p.url));
    };
    // revoke on unmount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addFiles = useCallback(
    (incoming: File[]) => {
      const imageFiles = incoming.filter((f) => f.type.startsWith("image/"));
      if (!imageFiles.length) return;

      setPreviews((prev) => {
        const next = multiple
          ? [
              ...prev,
              ...imageFiles.map((file) => ({
                file,
                url: URL.createObjectURL(file),
              })),
            ]
          : [{ file: imageFiles[0], url: URL.createObjectURL(imageFiles[0]) }];

        onChange?.(next.map((p) => p.file));
        return next;
      });
    },
    [multiple, onChange],
  );

  const removeFile = useCallback(
    (index: number) => {
      setPreviews((prev) => {
        URL.revokeObjectURL(prev[index].url);
        const next = prev.filter((_, i) => i !== index);
        onChange?.(next.map((p) => p.file));
        return next;
      });
    },
    [onChange],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files ?? []));
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  return (
    <div className={cn("p-2 w-full flex flex-col gap-2", className)}>
      <button
        type="button"
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={cn(
          "flex-1 flex flex-col gap-1 text-center justify-center items-center border border-dashed rounded-md w-full p-4 transition-colors cursor-pointer",
          isDragging
            ? "border-primary bg-primary/5"
            : "border-border bg-muted/20 hover:bg-muted/40",
        )}
      >
        <UploadCloudIcon className="size-4 text-muted-foreground" />
        <span className="text-muted-foreground text-xs font-medium">
          Ajouter des photos
        </span>
        <span className="text-xs text-muted-foreground/80">
          Glissez-déposez ou cliquez pour sélectionner
        </span>
      </button>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        className="hidden"
        onChange={handleInputChange}
      />

      {previews.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {previews.map((p, i) => (
            <div key={p.url} className="relative size-16 rounded-md overflow-hidden border border-border">
              <img
                src={p.url}
                alt=""
                className="size-full object-cover"
              />
              <button
                type="button"
                onClick={() => removeFile(i)}
                className="absolute top-0.5 right-0.5 size-4 bg-background/80 rounded-full flex items-center justify-center hover:bg-background"
              >
                <XIcon className="size-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
